import { ArrowUp, ChevronDown, ChevronUp, Download } from "lucide-react";
import React, { useEffect, useState } from "react";
import PermissionsGate from "../../../components/PermissionGate";
import { CustomButton } from "../../../components/ui/button/CustomButton/CustomButton";
import { Button } from "../../../components/ui/common/button";
import { FEATURES, SCOPES } from "../../../config/permission-maps";
import ChangeHistory from "./ChangeHistory";

const ProcessAnalysis: React.FC<{
  onScrollToGrid: () => void;
  downloadPdf: () => void;
  recipeResults: boolean;
  hasAnyUpdates: boolean;
  readOnly?: boolean;
  recipeId?: string;
}> = ({
  onScrollToGrid,
  downloadPdf,
  recipeResults,
  hasAnyUpdates,
  readOnly = false,
  recipeId
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isTechnicalCollapsed, setIsTechnicalCollapsed] = useState(false);

  useEffect(() => {
    setIsCollapsed(true);
    setIsTechnicalCollapsed(true);
    return () => {
      setIsCollapsed(false);
      setIsTechnicalCollapsed(false);
    };
  }, [recipeResults]);

  return (
    <div className="tw-mt-4">
      <div className="tw-rounded-lg tw-border tw-border-border tw-bg-background tw-w-full sm:tw-max-w-8xl tw-flex tw-flex-col tw-mx-auto tw-mt-2 tw-max-h-[600px]">
        <div
          className="tw-p-4 tw-pb-4.5 tw-flex tw-items-center tw-justify-between tw-cursor-pointer"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          <h2 className="tw-text-lg tw-font-display tw-font-bold tw-text-foreground">
            How you can use the results
          </h2>
          <button className="tw-text-foreground hover:tw-text-foreground/80 tw-transition-colors">
            {isCollapsed ? (
              <ChevronDown className="tw-h-5 tw-w-5" />
            ) : (
              <ChevronUp className="tw-h-5 tw-w-5" />
            )}
          </button>
        </div>
        {!isCollapsed && (
          <div className="tw-flex-1 tw-overflow-y-auto tw-px-6 tw-pb-5">
            <div className="tw-space-y-4 tw-text-sm tw-leading-relaxed tw-text-foreground/85 tw-pr-2">
              <div>
                <h3 className="tw-font-bold tw-mb-2.5">Use the simulator to:</h3>
                <ul className="tw-list-disc tw-pl-12 tw-space-y-1">
                  <li>
                    Compare ingredients See how the climate impact changes when using different
                    ingredient options
                  </li>
                  <li>
                    Discuss options with customers Use the results to show directional changes, such
                    as higher or lower impact and the approximate size of the potential difference
                  </li>
                  <li>
                    Identify lower-emission recipes Test different recipe scenarios to find
                    combinations with a potentially lower climate footprint{" "}
                  </li>
                </ul>
              </div>
              <p className="tw-mb-1.5">
                The results are intended for screening and comparative purposes only. For public
                communication or marketing claims, results must be supplemented with a full Life
                Cycle Assessment (LCA) and validated by a third party.
              </p>
              <div>
                <h3 className="tw-font-bold tw-mb-2.5">What is included in the calculation</h3>
                <p>
                  The simulator follows a cradle-to-gate approach, meaning it calculates the climate
                  impact (carbon footprint) of a product from raw material extraction (“cradle”) up
                  to the factory gate, before distribution, retail, or use. This includes:{" "}
                </p>
              </div>
              <ul className="tw-list-disc tw-pl-12 tw-space-y-1">
                <li>raw material production</li>
                <li>transport of ingredients</li>
                <li>energy use and processing during manufacturing</li>
              </ul>
              <p className="tw-mb-1.5">
                Packaging, distribution, use phase, and end-of-life are not included. This keeps the
                focus on recipe choices and production processes.
              </p>

              <p className="tw-mb-1.5">
                Where supplier-specific data is not available, the simulator uses standardised data
                and assumptions based on recognised life cycle databases and scientific literature.
                These data are selected and applied to be appropriate for the intended screening
                purpose of the tool.
              </p>
              <div>
                <h3 className="tw-font-bold tw-mb-2.5">Important limitations</h3>
                <ul className="tw-list-disc tw-pl-12 tw-space-y-1">
                  <li>
                    The results are estimates, not exact values. They are intended for comparing
                    recipe options, not for precise product declarations
                  </li>
                  <li>
                    Results should not be used as stand-alone numbers for public claims without
                    further validation
                  </li>
                  <li>
                    The tool provides directional guidance on climate impact, not a certified carbon
                    footprint, and this should be clearly communicated in stakeholder discussions
                  </li>
                </ul>
              </div>
              <p className="tw-mb-1.5">
                If there is any uncertainty about how results can be used externally or for making
                any public claims, please consult your internal sustainability team before sharing.
              </p>
              <h3 className="tw-font-bold tw-mb-2.5">Modelling approach and data </h3>
              <p className="tw-mb-1.5">
                The simulator is modelled by Nature Preserve using established methodologies and
                recognised data sources. Background data and assumptions are selected and
                periodically reviewed to be appropriate for the intended screening purpose of the
                tool.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="tw-rounded-lg tw-border tw-border-border tw-bg-background tw-w-full sm:tw-max-w-8xl tw-flex tw-flex-col tw-mx-auto tw-mt-3.5 tw-max-h-[600px]">
        <div
          className="tw-p-4 tw-pb-4.5 tw-flex tw-items-center tw-justify-between tw-cursor-pointer"
          onClick={() => setIsTechnicalCollapsed(!isTechnicalCollapsed)}
        >
          <h2 className="tw-text-lg tw-font-display tw-font-bold tw-text-foreground">
            Technical Methodological Description 
          </h2>
          <button className="tw-text-foreground hover:tw-text-foreground/80 tw-transition-colors">
            {isTechnicalCollapsed ? (
              <ChevronDown className="tw-h-5 tw-w-5" />
            ) : (
              <ChevronUp className="tw-h-5 tw-w-5" />
            )}
          </button>
        </div>
        {!isTechnicalCollapsed && (
          <div className="tw-flex-1 tw-overflow-y-auto tw-px-6 tw-pb-5">
            <div className="tw-space-y-4 tw-text-sm tw-leading-relaxed tw-text-foreground/85 tw-pr-2">
              <div>
                <h3 className="tw-font-bold tw-mb-2.5">1. Goal and scope</h3>
                <p className="tw-mb-1.5">
                  The Recipe Simulator provides screening-level estimates of the climate impact of
                  product formulations and enables comparison between alternative recipe scenarios.
                </p>
                <p className="tw-mb-1.5">
                  The methodology is based on ISO 14040/14044 principles and aligned with the
                  Environmental Footprint (EF) framework. An attributional LCA approach is applied.
                  The model is designed for comparative and decision-support purposes, not for
                  public claims without further validation.
                </p>
              </div>

              <div>
                <h3 className="tw-font-bold tw-mb-2.5">2. Functional unit </h3>
                <p className="tw-mb-1.5">The functional unit is defined as:</p>
                <ul className="tw-list-disc tw-pl-12 tw-space-y-1 tw-mb-1.5">
                  <li>1 kg of finished product at factory gate</li>
                </ul>
                <p className="tw-mb-1.5">
                  Results are expressed as kg CO₂eq per kg of finished product.
                </p>
                <p className="tw-mb-1.5">
                  Where relevant, results can be scaled using production volumes to estimate total
                  impact.
                </p>
              </div>

              <div>
                <h3 className="tw-font-bold tw-mb-2.5">3. System boundaries </h3>
                <p className="tw-mb-1.5">
                  The simulator follows a cradle-to-gate approach, meaning it calculates the climate
                  impact of a product from raw material extraction up to the factory gate, before
                  distribution, retail, use, or end-of-life. This includes:
                </p>
                <ul className="tw-list-disc tw-pl-12 tw-space-y-1 tw-mb-1.5">
                  <li>raw material production (cultivation and upstream processing)</li>
                  <li>transport of ingredients to the production site</li>
                  <li>manufacturing and processing energy</li>
                </ul>
                <p className="tw-mb-1.5">Excluded processes:</p>
                <ul className="tw-list-disc tw-pl-12 tw-space-y-1 tw-mb-1.5">
                  <li>primary and secondary packaging</li>
                  <li>distribution, use phase, and end-of-life</li>
                </ul>
                <p className="tw-mb-1.5">
                  Where supplier-specific data is unavailable, generic assumptions are applied
                  consistent with EF guidance to ensure completeness. The model applies datasets and
                  assumptions consistently across recipe scenarios to support comparability.
                </p>
              </div>

              <div>
                <h3 className="tw-font-bold tw-mb-2.5">4. Impact category </h3>
                <p className="tw-mb-1.5">The simulator currently assesses:</p>
                <ul className="tw-list-disc tw-pl-12 tw-space-y-1 tw-mb-1.5">
                  <li>Climate change impact (kg CO₂eq)</li>
                </ul>
                <p className="tw-mb-1.5">
                  Other impact categories are not included in the current version.
                </p>
              </div>

              <div>
                <h3 className="tw-font-bold tw-mb-2.5">5. Data sources</h3>
                <p className="tw-mb-1.5">The model uses a combination of:</p>
                <ul className="tw-list-disc tw-pl-12 tw-space-y-1 tw-mb-1.5">
                  <li>
                    Life cycle inventory databases (e.g. Ecoinvent, Agribalyse, Environmental
                    Footprint database)
                  </li>
                  <li>Literature-based values and standardised datasets</li>
                  <li>User-specific data, where available</li>
                </ul>
                <p className="tw-mb-1.5">Ingredient profiles may be:</p>
                <ul className="tw-list-disc tw-pl-12 tw-space-y-1 tw-mb-1.5">
                  <li>directly sourced from databases</li>
                  <li>adapted from existing datasets</li>
                  <li>manually modelled based on available data.</li>
                </ul>
                <p className="tw-mb-1.5">
                  The model prioritises consistency and comparability across scenarios.
                </p>
                <p className="tw-mb-1.5">
                  Manually modelled datasets have not been independently reviewed by a third party
                  and should be interpreted as screening-level estimates for comparative use.
                </p>
                <p className="tw-mb-1.5">
                  For externally sourced datasets, inherent differences in underlying third-party
                  methodologies may affect direct comparability.
                </p>
              </div>

              <div>
                <h3 className="tw-font-bold tw-mb-2.5">6. Allocation methodology </h3>
                <p className="tw-mb-1.5">
                  Environmental impacts for ingredients are derived from underlying life cycle
                  inventory datasets, which apply allocation based on either economic value or mass,
                  depending on the specific production system.
                </p>
                <p className="tw-mb-1.5">
                  The simulator adopts the allocation approaches embedded in the source databases
                  (e.g. Agribalyse, Ecoinvent). These approaches are generally consistent with ISO
                  14040/14044 principles for handling multi-functional processes.
                </p>
                <p className="tw-mb-1.5">
                  For manually modelled processes or ingredients, allocation follows standard LCA
                  practice and is applied consistently with the intended screening purpose of the
                  tool.
                </p>
              </div>

              <div>
                <h3 className="tw-font-bold tw-mb-2.5">7. Modelling of processing impacts </h3>
                <p className="tw-mb-1.5">
                  Processing impacts are estimated using either model-based calculations or
                  user-specific data inputs, depending on data availability.
                </p>
              </div>

              <div>
                <h3 className="tw-font-bold tw-mb-2.5">7.1 Model-based estimation</h3>
                <p className="tw-mb-1.5">
                  Processing energy requirements are modelled based on the thermodynamic and
                  physical behavior of the recipe composition, with a focus on heating, cooling, and
                  mixing operations. The main consumption items are electricity, heat, and water.
                  Through the user interface, users can select the country where food processing
                  takes place, and the location of the electricity supply network is determined
                  based on the selected country. The location of heat and water supply datasets is
                  selected based on regional proxies (e.g. for Europe or for Rest-of-World),
                  depending on the selected country.
                </p>
              </div>

              <div>
                <h3 className="tw-font-bold tw-mb-2.5">Thermal energy (heating)</h3>
                <p className="tw-mb-1.5">Thermal energy demand is calculated based on:</p>
                <ul className="tw-list-disc tw-pl-12 tw-space-y-1 tw-mb-1.5">
                  <li>ingredient-specific heat capacities</li>
                  <li>composition of water, fats, proteins, and carbohydrates</li>
                  <li>target processing temperature</li>
                </ul>
                <p className="tw-mb-1.5">
                  Steam energy requirements are estimated using standard industrial assumptions,
                  including latent heat values. The inventory of steam production includes:
                </p>
                <ul className="tw-list-disc tw-pl-12 tw-space-y-1">
                  <li>water</li>
                  <li>
                    natural gas and other sources (based on average fuel mix used for steam
                    production)
                  </li>
                  <li>electricity</li>
                </ul>
              </div>

              <div>
                <h3 className="tw-font-bold tw-mb-2.5">Thermal energy (cooling)</h3>
                <p className="tw-mb-1.5">
                  Similar to heating, the baseline heat to be removed is calculated based on the
                  product&apos;s specific recipe composition. The model estimates the electricity
                  and evaporative water consumed by factory chillers across three distinct cooling
                  scenarios:
                </p>
                <ul className="tw-list-disc tw-pl-12 tw-space-y-1 tw-mb-2.5">
                  <li>
                    In-Line Cooling (Agitated Tank): The product is cooled in bulk. The model
                    accounts for reduced chiller efficiency as thick, cold food clings to the inside
                    of the tank (boundary layer insulation).
                  </li>
                  <li>
                    Post-Packaging (Walk-In Cooler): Slow cooling in standard cold storage. Because
                    the food is sealed and static, cooling time relies heavily on the physical size
                    of the container and heat conducting slowly out of the product.
                  </li>
                  <li>
                    Post-Packaging (Blast Chiller): Fast cooling using high-velocity, sub-zero air.
                    While it cools much faster, it incurs a higher electricity penalty due to the
                    heavy use of blower fans and compressors working harder to maintain freezing
                    temperatures.
                  </li>
                  <li>
                    Extended Cold Storage: The product is held at its final target temperature over
                    a specified duration. The model calculates the energy required strictly to
                    maintain this cold state by fighting standard facility heat leaks (e.g., through
                    walls or open warehouse doors). This energy load scales based on how cold the
                    room is kept, but entirely ignores product thickness since the food is
                    completely static.
                  </li>
                </ul>

                <div className="tw-overflow-x-auto tw-mb-2.5">
                  <table className="tw-w-auto tw-table-auto tw-text-left tw-text-xs tw-leading-tight tw-border tw-border-border">
                    <thead>
                      <tr className="tw-border-b tw-border-border">
                        <th className="tw-p-1 tw-font-semibold">Variable / Considered Factor</th>
                        <th className="tw-p-1 tw-font-semibold">In-Line Cooling (Tank)</th>
                        <th className="tw-p-1 tw-font-semibold">Post-Packaging: Walk-In</th>
                        <th className="tw-p-1 tw-font-semibold">Post-Packaging: Blast Chiller</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="tw-border-b tw-border-border/70">
                        <td className="tw-p-1">Specific Heat Capacity</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                      </tr>
                      <tr className="tw-border-b tw-border-border/70">
                        <td className="tw-p-1">Total Product Mass</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                      </tr>
                      <tr className="tw-border-b tw-border-border/70">
                        <td className="tw-p-1">Chiller COP (Efficiency)</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                      </tr>
                      <tr className="tw-border-b tw-border-border/70">
                        <td className="tw-p-1">Thermal Conductivity</td>
                        <td className="tw-p-1 tw-text-center">-</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                      </tr>
                      <tr className="tw-border-b tw-border-border/70">
                        <td className="tw-p-1">Container Geometry (Radius)</td>
                        <td className="tw-p-1 tw-text-center">-</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                      </tr>
                      <tr className="tw-border-b tw-border-border/70">
                        <td className="tw-p-1">Convective Airflow (h)</td>
                        <td className="tw-p-1 tw-text-center">-</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                      </tr>
                      <tr className="tw-border-b tw-border-border/70">
                        <td className="tw-p-1">Structural Modifiers (Starch/Fat)</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                        <td className="tw-p-1 tw-text-center">-</td>
                        <td className="tw-p-1 tw-text-center">-</td>
                      </tr>
                      <tr className="tw-border-b tw-border-border/70">
                        <td className="tw-p-1">Mechanical Motor Energy</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                        <td className="tw-p-1 tw-text-center">-</td>
                        <td className="tw-p-1 tw-text-center">-</td>
                      </tr>
                      <tr className="tw-border-b tw-border-border/70">
                        <td className="tw-p-1">Cold Maintenance / Holding</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                        <td className="tw-p-1 tw-text-center">-</td>
                        <td className="tw-p-1 tw-text-center">-</td>
                      </tr>
                      <tr className="tw-border-b tw-border-border/70">
                        <td className="tw-p-1">Ambient Heat Infiltration</td>
                        <td className="tw-p-1 tw-text-center">V</td>
                        <td className="tw-p-1 tw-text-center">-*</td>
                        <td className="tw-p-1 tw-text-center">-*</td>
                      </tr>
                      <tr>
                        <td className="tw-p-1">Freezing / Phase Change</td>
                        <td className="tw-p-1 tw-text-center">-**</td>
                        <td className="tw-p-1 tw-text-center">-**</td>
                        <td className="tw-p-1 tw-text-center">-**</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <p className="tw-mb-1.5 tw-text-xs tw-italic">
                  *Post-Packaging models assume the facility HVAC is correctly sized to maintain the
                  set room temperature, calculating only the direct product cooling load.
                </p>
                <p className="tw-mb-1.5 tw-text-xs tw-italic">
                  **All models explicitly restrict target temperatures to &gt;1°C to avoid the
                  Latent Heat of Fusion (ice formation).
                </p>
              </div>

              <div>
                <h3 className="tw-font-bold tw-mb-2.5">Mechanical energy (mixing) </h3>
                <p className="tw-mb-1.5">
                  Mechanical energy demand is estimated dynamically based on changes in thickness
                  (viscosity) during both heating and cooling.
                </p>
                <p className="tw-mb-1.5">Key assumptions include:</p>
                <ul className="tw-list-disc tw-pl-12 tw-space-y-1">
                  <li>melting of fats reduces mechanical resistance (~35°C)</li>
                  <li>starch gelatinisation increases viscosity (~60°C)</li>
                  <li>protein structuring contributes to further thickening (~75°C)</li>
                  <li>
                    cold maintenance holding demands peak mechanical energy, as fully cooled fats,
                    starches, and proteins reach their maximum possible thickness
                  </li>
                </ul>
                <p className="tw-mb-1.5">
                  A baseline mechanical load is applied and adjusted according to composition and
                  temperature-dependent behavior.
                </p>
              </div>

              <div>
                <h3 className="tw-font-bold tw-mb-2.5">Process assumptions </h3>
                <p className="tw-mb-1.5">Default industrial parameters include:</p>
                <ul className="tw-list-disc tw-pl-12 tw-space-y-1 tw-mb-1.5">
                  <li>
                    batch volume: 10 m<sup>3</sup>
                  </li>
                  <li>container size (post-packaging): 80 mm diameter (standard jar/tub)</li>
                  <li>
                    heat source: steam (boiler capacity: 2,500 kg/hour; energy value: ~2,600 kJ/kg)
                  </li>
                  <li>ambient cooling environments: Walk-In Cooler (2°C), Blast Chiller (-5°C)</li>
                  <li>
                    safety limits: Cooling calculations are restricted to target temperatures
                    &gt;=1°C to explicitly exclude the energy spikes associated with freezing water
                    into ice.
                  </li>
                  <li>heat loss: up to 10 kW at peak temperature</li>
                  <li>
                    baseline mixing load: ~0.2 kW/m<sup>3</sup>
                  </li>
                  <li>mechanical energy source: electricity, medium voltage</li>
                </ul>
                <p className="tw-mb-1.5">
                  These assumptions represent standardised industrial processing conditions based on
                  literature and engineering references and are not site-specific.
                </p>
              </div>

              <div>
                <h3 className="tw-font-bold tw-mb-2.5">7.2 User-specific data </h3>
                <p className="tw-mb-1.5">
                  Where available, user-provided processing data (e.g. measured energy use or
                  equipment specifications) can be used to replace or refine the model-based
                  estimates. This enables results to be progressively improved as more detailed,
                  site-specific data becomes available.
                </p>
              </div>

              <div>
                <h3 className="tw-font-bold tw-mb-2.5">8. Limitations </h3>
                <ul className="tw-list-disc tw-pl-12 tw-space-y-1 tw-mb-1.5">
                  <li>
                    Results are screening-level estimates, and do not represent full life cycle
                    assessments.
                  </li>
                  <li>
                    The model relies on generic and semi-specific data where primary data is
                    unavailable.
                  </li>
                  <li>
                    Processing assumptions reflect standardised conditions, not specific factory
                    configurations or product declarations.
                  </li>
                  <li>
                    Post-packaging cooling calculates the direct product energy load and does not
                    include full facility HVAC overhead, such as empty warehouse space or open
                    doors.
                  </li>
                  <li>
                    Results are intended for comparative use, not precise product declarations.
                  </li>
                </ul>
                <p className="tw-mb-1.5">
                  For public communication or marketing claims, a full LCA using product-specific
                  data and third-party validation is required.
                </p>
              </div>

              <div>
                <h3 className="tw-font-bold tw-mb-2.5">9. References </h3>
                <ul className="tw-list-disc tw-pl-12 tw-space-y-1">
                  <li>Choi, Y., &amp; Okos, M. R. (1986) - Thermal properties of foods</li>
                  <li>Steffe, J. F. (1996) - Rheological methods in food process engineering</li>
                  <li>McCabe, W. L. et al. (2004) - Unit operations of chemical engineering</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
      <ChangeHistory recipeId={recipeId} readOnly={readOnly} />

      {recipeResults && (
        <div className="tw-w-full sm:tw-max-w-8xl tw-mx-auto tw-mt-8 tw-rounded-lg tw-mb-5">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
            <CustomButton
              onClick={onScrollToGrid}
              buttonOutLine
              iconWithText
              wrapInColumn={false}
              className="!tw-h-11 !tw-w-auto tw-gap-2 tw-px-6"
              icon
              iconChild={<ArrowUp className="tw-h-4 tw-w-4" />}
              title=" Navigate to Top"
              theme="lightgrey"
            />
            <PermissionsGate scopes={[SCOPES.canDownload]} feature={FEATURES.recipeSimulator}>
              <Button
                onClick={downloadPdf}
                size="lg"
                className="tw-h-11 tw-gap-2 tw-px-6 tw-text-base"
                disabled={hasAnyUpdates}
              >
                <Download className="tw-h-4 tw-w-4" />
                Download Report
              </Button>
            </PermissionsGate>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProcessAnalysis;
